import { Link as RouterLink } from "react-router-dom";

import BusinessIcon from "@mui/icons-material/Business";
import LaunchIcon from "@mui/icons-material/Launch";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import type { SxProps, Theme } from "@mui/material/styles";

import useSunoLeadsAccess from "hooks/use-suno-leads-access.hook";
import type { CallContact, PracticeCallPatient } from "services/dialstack";

import { isPatientRemovable } from "./caller-identity.util";
import type { UnknownCallerActions as Actions } from "./use-unknown-caller-actions.hook";

export const WAITING_FOR_CALL_TOOLTIP = "Waiting for call details";

type UnknownCallerActionsProps = {
  actions: Actions;
  patient?: PracticeCallPatient | null;
  contact?: CallContact | null;
  // How the call got its patient: a manual attach (and a click-to-call row,
  // which has no match) can be undone here; a patient-phone match cannot.
  matchedOn?: string | null;
  size?: "small" | "medium";
  buttonSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  justifyContent?: "flex-start" | "center";
};

// Quick add · Attach to patient · Save to Rolodex · Save as lead. Once the
// call has a patient the row offers only Remove from patient, and only for a
// patient staff attached by hand (ENG-2563); a contact-matched call offers the
// patient routes plus a way to correct the contact (or, with the Rolodex, to
// take it off the call). Save as lead is always on the row: Suno Leads is an
// upsell, so a practice without it sees the button disabled with the reason.
export default function UnknownCallerActions({
  actions,
  patient,
  contact,
  matchedOn,
  size = "small",
  buttonSx,
  sx,
  justifyContent = "flex-start"
}: UnknownCallerActionsProps) {
  const leadsAccess = useSunoLeadsAccess();
  const waiting = actions.callId === null;
  if (patient) {
    if (!isPatientRemovable(matchedOn)) return null;
    return (
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        justifyContent={justifyContent}
        data-testid="unknown-caller-actions"
        sx={sx}
      >
        <Button
          size={size}
          variant="outlined"
          startIcon={<LinkOffIcon />}
          onClick={actions.removePatient}
          disabled={waiting || actions.isDetaching}
          sx={buttonSx}
          data-testid="unknown-caller-removePatient"
        >
          Remove from patient
        </Button>
      </Stack>
    );
  }

  const buttons = contact
    ? [
        {
          key: "attach",
          label: "Attach to patient",
          icon: <LinkIcon />,
          onClick: actions.openAttach
        },
        {
          key: "contact",
          label: "Change contact",
          icon: <BusinessIcon />,
          onClick: actions.openSaveContact
        },
        {
          key: "detach",
          label: "Remove contact",
          icon: <LinkOffIcon />,
          onClick: actions.removeContact
        }
      ]
    : [
        {
          key: "quickAdd",
          label: "Quick add",
          icon: <PersonAddIcon />,
          onClick: actions.openQuickAdd
        },
        {
          key: "attach",
          label: "Attach to patient",
          icon: <LinkIcon />,
          onClick: actions.openAttach
        },
        {
          // The Rolodex names the destination once the practice has it; off,
          // the wording stays what it was (the gate's off state is unchanged).
          key: "contact",
          label: "Save to Rolodex",
          icon: <BusinessIcon />,
          onClick: actions.openSaveContact
        }
      ];

  // Why the lead button is disabled, when it is for a Leads reason. While
  // waiting on the call id the row's own tooltip says so for every button.
  const leadReason =
    !waiting && !actions.leadId && !leadsAccess.enabled
      ? leadsAccess.message
      : null;
  const leadButton = actions.leadId ? (
    <Button
      component={RouterLink}
      to={`/leads/list/${actions.leadId}`}
      size={size}
      variant="outlined"
      startIcon={<LaunchIcon />}
      sx={buttonSx}
      data-testid="unknown-caller-view-lead"
    >
      View lead
    </Button>
  ) : (
    <Button
      size={size}
      variant="outlined"
      startIcon={<PersonSearchIcon />}
      onClick={actions.openSaveLead}
      disabled={waiting || !leadsAccess.enabled}
      sx={buttonSx}
      data-testid="unknown-caller-lead"
    >
      Save as lead
    </Button>
  );

  const row = (
    <Stack
      direction="row"
      spacing={0.75}
      useFlexGap
      flexWrap="wrap"
      justifyContent={justifyContent}
      data-testid="unknown-caller-actions"
      sx={sx}
    >
      {buttons.map(({ key, label, icon, onClick }) => (
        <Button
          key={key}
          size={size}
          variant="outlined"
          startIcon={icon}
          onClick={onClick}
          disabled={waiting || (key === "detach" && actions.isDetaching)}
          sx={buttonSx}
          data-testid={`unknown-caller-${key}`}
        >
          {label}
        </Button>
      ))}
      {!contact &&
        (leadReason ? (
          // A disabled button swallows pointer events and cannot take focus,
          // so the reason hangs off a focusable wrapper that the tooltip
          // describes (`describeChild` → aria-describedby while open).
          <Tooltip title={leadReason} describeChild>
            <Box
              component="span"
              tabIndex={0}
              sx={{
                display: "inline-flex",
                borderRadius: 1,
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2
                }
              }}
              data-testid="unknown-caller-lead-unavailable"
            >
              {leadButton}
            </Box>
          </Tooltip>
        ) : (
          leadButton
        ))}
    </Stack>
  );

  // Disabled buttons swallow pointer events, so the tooltip wraps the row.
  return waiting ? (
    <Tooltip title={WAITING_FOR_CALL_TOOLTIP}>
      <span>{row}</span>
    </Tooltip>
  ) : (
    row
  );
}
